import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { conversationKeys, conversationQueryKey } from "../../../agent";
import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import { Button } from "../../../ui/Button";
import { agentUnavailableSentence } from "../../../ui/text";
import { useCurrentMe } from "../../../app/auth";

export const EXPLAIN_CHIPS = [
  { id: "why", label: "Why this?", question: "Why is this card here?" },
  { id: "wrong", label: "What could be wrong?", question: "What could be wrong with this card?" },
  { id: "library", label: "What does the library say?", question: "What does the library say about this?" },
  { id: "used", label: "Where else is it used?", question: "Where else is this used?" },
] as const;

/**
 * The four questions an SME asks most. Each one goes to the review helper (the agent in SME review mode) with the card
 * it is about, and the answer arrives in the panel beside the card.
 */
export function ExplainChips({ elementId, onAsked }: { elementId: string; /** The question went to the review helper: open its panel. */ onAsked?: () => void }) {
  const me = useCurrentMe();
  const queryClient = useQueryClient();
  const post = useApiCommand("post_message");
  const [problem, setProblem] = useState<string | null>(null);
  const membershipId = me.active?.membership_id;
  const available = me.agent.available;

  if (!membershipId) return null;
  const key = conversationKeys.sme(membershipId);

  const ask = async (question: string) => {
    setProblem(null);
    try {
      await post.mutateAsync({ key, text: question, context: { screen: "sme_inbox", element_id: elementId } });
      void queryClient.invalidateQueries({ queryKey: conversationQueryKey(key, false) });
      onAsked?.();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <div className="ra-chips" role="group" aria-label="Ask the review helper about this card">
      <div className="row">
        {EXPLAIN_CHIPS.map((chip) => (
          <Button key={chip.id} variant="secondary" className="ra-chip" disabled={!available || post.isPending} onClick={() => void ask(chip.question)}>
            {chip.label}
          </Button>
        ))}
      </div>
      {!available && <p className="muted">{agentUnavailableSentence(me.agent.reason)} You can still decide on the card.</p>}
      {problem && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}
    </div>
  );
}
